/**
 * Storefront: the customer app's API at the table (03-modules.md §8, 05-api.md
 * "Customer API", 06-flows.md F3; M3 task 6). Customers are their own actor kind:
 * a diner scans the table QR, joins the table's visit (a table session) with no
 * login, orders from the customer menu (ids and quantities only; the server prices),
 * follows the order, asks for the bill or pays online, and rates it.
 *
 * Credentials (`X-Table-Token`):
 * - the printed table QR token (org, `t1.…`) opens or joins the table's session
 *   (`POST /v1/store/table-sessions`), and resolves the table (`GET /v1/store/t/{token}`);
 * - every other call carries the diner token that call returns (`d1.…`): bound to
 *   one table session and one device, it expires, and it stops ordering once the
 *   visit ends (the order is read and rated with it a while longer).
 */
import { z } from 'zod';
import { isoTimestampSchema } from './common.js';
import { defineEvent } from './events.js';
import { uuidSchema } from './ids.js';
import { nonNegativePaiseSchema, paiseSchema } from './money.js';
import {
  FULFILMENT_STATUSES,
  LINE_STATUSES,
  ORDER_STATUSES,
  orderLineInputSchema,
  qtyMilliSchema,
} from './orders.js';
import { PRICE_CONTEXTS, THEME_IDS } from './org.js';
import { CONFIRMATION_REJECTIONS, INTENT_METHODS, INTENT_STATUSES } from './payments.js';

/** The longest a printed table QR token or a diner token may be (both are ~100–200 chars). */
export const MAX_TABLE_TOKEN_LENGTH = 400;

/** At most this many lines in one customer order or round. */
export const MAX_STORE_LINES_PER_ORDER = 50;

/** At most this many of one item on a customer's line (abuse guard; staff can add more). */
export const MAX_STORE_QTY_MILLI = 50_000;

/** Why a table session ended. */
export const TABLE_SESSION_CLOSE_REASONS = [
  /** Every order of the visit is closed, cancelled or rejected. */
  'orders_ended',
  /** Nobody ordered and nothing happened for the idle time. */
  'idle',
  /** A staff member ended it at the till ("clear table"). */
  'staff',
] as const;
export type TableSessionCloseReason = (typeof TABLE_SESSION_CLOSE_REASONS)[number];

export const tableTokenParamsSchema = z.object({
  tableToken: z.string().min(1).max(MAX_TABLE_TOKEN_LENGTH),
});

export const storeOrderParamsSchema = z.object({ orderId: uuidSchema });

export const storeOutletParamsSchema = z.object({ outletId: uuidSchema });

/** What ordering at this table allows (outlet settings, 03-modules.md §8). */
export const storeOrderingSchema = z.object({
  /** `qr.ordering`: off means the menu only. */
  enabled: z.boolean(),
  /** `order.require_acceptance.qr`: orders wait for the outlet to accept them. */
  requiresAcceptance: z.boolean(),
  /** `qr.pay_online`: "pay now" is offered (paying at the counter always is). */
  payOnline: z.boolean(),
});

/** `GET /v1/store/t/{tableToken}`: what a printed table QR points at. */
export const storeTableSchema = z.object({
  outlet: z.object({
    id: uuidSchema,
    name: z.string(),
    /** False when the outlet is suspended or closed: orders are refused (OUTLET_CLOSED). */
    open: z.boolean(),
    theme: z.enum(THEME_IDS),
  }),
  table: z.object({
    id: uuidSchema,
    name: z.string(),
    seats: z.int(),
    sectionId: uuidSchema,
    sectionName: z.string(),
    /** False when the table is out of service: orders are refused. */
    inService: z.boolean(),
  }),
  /** The menu context the table orders in (its section's pricing context). */
  context: z.enum(PRICE_CONTEXTS),
  ordering: storeOrderingSchema,
  /** The table's visit in progress, if any (joining it shares its order). */
  openSession: z
    .object({ id: uuidSchema, openedAt: isoTimestampSchema, hasOrder: z.boolean() })
    .nullable(),
});

/** `POST /v1/store/table-sessions` (with the printed QR token as X-Table-Token). */
export const openTableSessionSchema = z.object({
  /**
   * The device's own id (a UUID it keeps): joining again from the same device reuses
   * its place at the table instead of adding another diner.
   */
  deviceId: uuidSchema.optional(),
});

export const storeOrderLineSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema,
  variantId: uuidSchema.nullable(),
  name: z.string(),
  qtyMilli: z.int(),
  unitPricePaise: nonNegativePaiseSchema,
  lineTotalPaise: paiseSchema,
  status: z.enum(LINE_STATUSES),
  note: z.string().nullable(),
  modifiers: z.array(
    z.object({
      kind: z.enum(['option', 'addon']),
      /** The option or add-on's id (a reorder rebuilds the choices from it). */
      refId: uuidSchema,
      name: z.string(),
      qtyMilli: z.int(),
      unitPricePaise: nonNegativePaiseSchema,
    }),
  ),
});

/** An order as its diner sees it: no staff, terminals, notes of voids or tags. */
export const storeOrderSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  tableSessionId: uuidSchema.nullable(),
  orderNo: z.int(),
  status: z.enum(ORDER_STATUSES),
  fulfilmentStatus: z.enum(FULFILMENT_STATUSES),
  subtotalPaise: paiseSchema,
  discountPaise: paiseSchema,
  chargesPaise: paiseSchema,
  taxPaise: paiseSchema,
  roundOffPaise: paiseSchema,
  totalPaise: paiseSchema,
  paidPaise: paiseSchema,
  duePaise: paiseSchema,
  /** Live items (voided ones are left out). */
  lines: z.array(storeOrderLineSchema),
  charges: z.array(z.object({ name: z.string(), amountPaise: paiseSchema, taxPaise: paiseSchema })),
  taxes: z.array(z.object({ component: z.string(), rateBp: z.int(), taxPaise: paiseSchema })),
  /** The GST invoice was issued (settled). */
  invoiced: z.boolean(),
  /** The GST invoice number (e.g. `IND/2627/15`) once the order is settled; null before. */
  invoiceNo: z.string().nullable(),
  billRequestedAt: isoTimestampSchema.nullable(),
  rating: z.object({ rating: z.int(), comment: z.string().nullable() }).nullable(),
  createdAt: isoTimestampSchema,
  billedAt: isoTimestampSchema.nullable(),
  settledAt: isoTimestampSchema.nullable(),
  version: z.int(),
});
export type StoreOrderView = z.output<typeof storeOrderSchema>;

/** The diner's table visit. */
export const dinerSessionSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  outletName: z.string(),
  tableId: uuidSchema,
  tableName: z.string(),
  sectionId: uuidSchema,
  context: z.enum(PRICE_CONTEXTS),
  status: z.enum(['open', 'closed']),
  openedAt: isoTimestampSchema,
  closedAt: isoTimestampSchema.nullable(),
  billRequestedAt: isoTimestampSchema.nullable(),
  /** This device's place at the table. */
  customerSessionId: uuidSchema,
  /** How many devices joined the visit. */
  diners: z.int(),
  ordering: storeOrderingSchema,
  /** The visit's orders, oldest first (normally one: rounds add to it). */
  orders: z.array(storeOrderSchema),
});
export type DinerSessionView = z.output<typeof dinerSessionSchema>;

/** `POST /v1/store/table-sessions`: the diner token and the visit. */
export const dinerTokenSchema = z.object({
  /** Send as `X-Table-Token` on every other /v1/store call, and as `{ tableToken }` on /ws. */
  token: z.string(),
  expiresAt: isoTimestampSchema,
  session: dinerSessionSchema,
});

/** A line as the customer app sends it: what was chosen, never a price. */
export const storeLineInputSchema = orderLineInputSchema.omit({ seat: true, course: true }).extend({
  qtyMilli: qtyMilliSchema.max(MAX_STORE_QTY_MILLI),
  addons: z
    .array(
      z.object({
        addonId: uuidSchema,
        /** Per one unit of the line; default 1000, at most 10. */
        qtyMilli: qtyMilliSchema.max(10_000).optional(),
      }),
    )
    .max(20)
    .refine((v) => new Set(v.map((a) => a.addonId)).size === v.length, {
      message: 'Each add-on at most once',
    })
    .optional(),
});

/**
 * `POST /v1/store/outlets/{o}/orders`: place an order at the diner's table, or add a
 * round to the visit's running order. Items only; the server prices them.
 */
export const placeStoreOrderSchema = z.object({
  lines: z
    .array(storeLineInputSchema)
    .min(1)
    .max(MAX_STORE_LINES_PER_ORDER)
    .refine(
      (v) => {
        const ids = v.flatMap((l) => (l.id === undefined ? [] : [l.id]));
        return new Set(ids).size === ids.length;
      },
      { message: 'Each line id at most once' },
    ),
  /** People at the table (first order only). */
  covers: z.int().min(1).max(99).optional(),
  /** Optional, for the receipt (03-modules.md §8: phone only for receipts, loyalty, delivery). */
  customer: z
    .object({
      name: z.string().trim().min(1).max(80).optional(),
      phone: z
        .string()
        .trim()
        .regex(/^\+?\d{6,15}$/, { message: 'Expected a phone number' })
        .optional(),
    })
    .optional(),
});

/** `POST /v1/store/orders/{id}/request-bill`. */
export const requestBillSchema = z.object({});

/** `POST /v1/store/orders/{id}/pay`: pay what is due online. */
export const storePaySchema = z.object({
  methods: z.array(z.enum(INTENT_METHODS)).min(1).max(3).optional(),
});

/** What the customer app needs to collect a payment (never secrets). */
export const storePaymentRequestSchema = z.object({
  id: uuidSchema,
  orderId: uuidSchema,
  amountPaise: paiseSchema,
  status: z.enum(INTENT_STATUSES),
  expired: z.boolean(),
  expiresAt: isoTimestampSchema,
  upiIntentUrl: z.string().nullable(),
  qrPayload: z.string().nullable(),
  qrImageUrl: z.string().nullable(),
  checkout: z.record(z.string(), z.unknown()).nullable(),
  failureReason: z.string().nullable(),
  rejectionReason: z.enum(CONFIRMATION_REJECTIONS).nullable(),
});

/** What a verified payment did to the order (the app refetches the order for the rest). */
export const storeVerifyResultSchema = z.object({
  payment: storePaymentRequestSchema,
  order: z.object({
    id: uuidSchema,
    status: z.enum(ORDER_STATUSES),
    totalPaise: paiseSchema,
    paidPaise: paiseSchema,
    duePaise: paiseSchema,
    /** Paid in full and the GST invoice issued. */
    invoiced: z.boolean(),
  }),
});

export const storePayParamsSchema = z.object({ orderId: uuidSchema, intentId: uuidSchema });

/** `POST /v1/store/orders/{id}/pay/{intentId}/verify`: what the gateway checkout returned. */
export const storeVerifySchema = z.object({
  /** Passed to the gateway's connector as is. */
  result: z.record(z.string().max(100), z.string().max(1000)),
});

/** `POST /v1/store/orders/{id}/rate`. */
export const rateOrderSchema = z.object({
  rating: z.int().min(1).max(5),
  comment: z.string().trim().max(500).optional(),
});

export const orderRatingSchema = z.object({
  orderId: uuidSchema,
  rating: z.int(),
  comment: z.string().nullable(),
  createdAt: isoTimestampSchema,
});

// ---------------------------------------------------------------------------
// Staff side: the table visits at an outlet (the till shows bill requests).

export const tableSessionSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  tableId: uuidSchema,
  status: z.enum(['open', 'closed']),
  openedAt: isoTimestampSchema,
  closedAt: isoTimestampSchema.nullable(),
  closeReason: z.enum(TABLE_SESSION_CLOSE_REASONS).nullable(),
  billRequestedAt: isoTimestampSchema.nullable(),
  lastActivityAt: isoTimestampSchema,
  diners: z.int(),
  orderIds: z.array(uuidSchema),
  version: z.int(),
});

export const tableSessionListQuerySchema = z.object({
  status: z.enum(['open', 'closed']).optional(),
});

export const tableSessionListSchema = z.object({ data: z.array(tableSessionSchema) });

export const tableSessionParamsSchema = z.object({
  outletId: uuidSchema,
  tableSessionId: uuidSchema,
});

// ---------------------------------------------------------------------------
// Events (projected to outlet:{o}:pos; none carries personal data)

const sessionRef = {
  tableSessionId: uuidSchema,
  tableId: uuidSchema,
};

export const tableSessionOpenedV1 = defineEvent(
  'table_session.opened',
  1,
  z.object({ ...sessionRef, sectionId: uuidSchema, openedAt: isoTimestampSchema }),
);

export const tableSessionJoinedV1 = defineEvent(
  'table_session.joined',
  1,
  z.object({ ...sessionRef, customerSessionId: uuidSchema, diners: z.int() }),
);

export const tableSessionClosedV1 = defineEvent(
  'table_session.closed',
  1,
  z.object({
    ...sessionRef,
    reason: z.enum(TABLE_SESSION_CLOSE_REASONS),
    orderIds: z.array(uuidSchema),
  }),
);

export const billRequestedV1 = defineEvent(
  'table_session.bill_requested',
  1,
  z.object({
    ...sessionRef,
    orderId: uuidSchema,
    orderNo: z.int(),
    status: z.enum(ORDER_STATUSES),
    totalPaise: paiseSchema,
    duePaise: paiseSchema,
  }),
);

export const orderRatedV1 = defineEvent(
  'order.rated',
  1,
  z.object({
    orderId: uuidSchema,
    orderNo: z.int(),
    tableSessionId: uuidSchema.nullable(),
    rating: z.int().min(1).max(5),
    comment: z.string().nullable(),
  }),
);

export const STOREFRONT_EVENTS = [
  tableSessionOpenedV1,
  tableSessionJoinedV1,
  tableSessionClosedV1,
  billRequestedV1,
  orderRatedV1,
] as const;
